import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SellApartmentFlowPaths } from 'routes';
import { createSaleAd } from 'api';
import { MarketTypes } from '../../api/types';
import useFacilityConstants from '../../api/hooks/use-facility-constants';
import useApartmentSaleInfo, { APIApartmentSellInfoRes } from 'api/hooks/use-apartment-sale-info';
import useBoundStore from 'store';
import BaseScreen from 'components/base-screen';
import BottomSheet from 'components/bottom-sheet';
import Button, { ButtonProps } from 'components/button';
import BottomSheetResult from 'components/bottom-sheet-result';
import ApartmentListItem from 'components/apartment-list-item';
import PriceCalculator from '../../components/price-calculator';
import styles from './styles.module.scss';

export type InputApartmentPriceNavigateProps = {
  apartment: APIApartmentSellInfoRes & {
    id: number;
    price: number;
    hasBurden: boolean;
    hasMaternityCapital: boolean;
    loweredPrice: number;
  };
};

const ChoosePriceSecondary: React.FC = () => {
  const navigate = useNavigate();
  const reportConstant = useFacilityConstants();
  const [createdSellApartmentId, setCreatedSellApartmentId] = useState<number>();

  const selectedSellComplex = useBoundStore((s) => s.selectedSellComplex);
  const resetSellFlowSecondary = useBoundStore((s) => s.resetSellFlowSecondary);
  // const sellRoomCountSecondary = useBoundStore((s) => s.sellRoomCountSecondary);
  const typeId = useBoundStore((s) => s.typeIdSecondary);
  const forceAuthorize = useBoundStore((s) => s.forceAuthorize);

  const selectedSellBuildingSecondary = useBoundStore((s) => s.selectedSellBuildingSecondary);
  const sellApartmentNumberSecondary = useBoundStore((s) => s.sellApartmentNumberSecondary);
  const sellAreaInSqMSecondary = useBoundStore((s) => s.sellAreaInSqMSecondary);

  const navigationProps = useLocation().state as InputApartmentPriceNavigateProps | undefined;

  const saleInfo = useApartmentSaleInfo(
    selectedSellBuildingSecondary.id,
    navigationProps ? '' : sellApartmentNumberSecondary,
  );

  const apartmentSellInfo = saleInfo.apartmentSellInfo;

  interface IPriceStat {
    days: number;
    price: number;
    percent: number;
  }

  const [selectedPrice, setSelectedPrice] = useState<{
    stat: IPriceStat;
    value: number;
  } | null>(null);

  const [priceWithoutMarketPrise, setPriceWithoutMarketPrise] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const buttons: ButtonProps[] = [
    {
      label: 'Разместить объявление',
      variant: 'secondary',
      loading: isSubmitting,
      disabled: !(selectedPrice?.stat?.price || priceWithoutMarketPrise),
      onClick: async () => {
        if (!forceAuthorize()) return;
        setIsSubmitting(true);
        await createSaleAd({
          // roomCount: sellRoomCountSecondary,
          buildingId: selectedSellBuildingSecondary.id,
          apartmentNumber: sellApartmentNumberSecondary,
          areaInSqM: sellAreaInSqMSecondary,
          price: Math.floor(
            // @ts-ignore
            !!saleInfo?.apartmentSellInfo ? selectedPrice?.stat?.price! : +selectedPrice?.value,
          ),
          expiresInDays: !!saleInfo?.apartmentSellInfo ? selectedPrice?.stat?.days! : 40,
          marketType: MarketTypes.SECONDARY,
          typeId: typeId,
        }).then((data) => {
          try {
            setCreatedSellApartmentId(data?.result?.id);
            resetSellFlowSecondary();
            setIsBottomSheetOpen(true);
          } catch {}
        });
        setIsSubmitting(false);
      },
    },
  ];

  useEffect(() => {}, [priceWithoutMarketPrise]);

  const handleDismissBottomSheet = () => {
    setIsBottomSheetOpen(false);
    navigate(SellApartmentFlowPaths.mainSellChooseMode);
  };

  const handleUploadPhoto = () => {
    setIsBottomSheetOpen(false);
    navigate(SellApartmentFlowPaths.uploadPhotoSecondary, {
      state: { id: createdSellApartmentId },
    });
  };

  return (
    <BaseScreen
      padContent
      title='Установите цену. Вторичка'
      buttons={buttons}
      onBackButtonClick={() => navigate(-1)}
      floatingButtons={false}
    >
      <div className={styles.container}>
        <ApartmentListItem
          isFavorite={false}
          price={priceWithoutMarketPrise}
          squarePrice={
            apartmentSellInfo?.areaInSqM && priceWithoutMarketPrise
              ? ~~(priceWithoutMarketPrise / apartmentSellInfo.areaInSqM)
              : undefined
          }
          image={apartmentSellInfo?.planImage ?? ''}
          rooms={apartmentSellInfo?.roomCount ?? 0}
          area={apartmentSellInfo?.areaInSqM ?? 0}
          housingEstate={selectedSellComplex.title}
          floor={
            apartmentSellInfo
              ? { floor: apartmentSellInfo.floor, total: apartmentSellInfo.floorCount }
              : { floor: 0, total: 0 }
          }
        />
        <PriceCalculator
          report={saleInfo?.apartmentSellInfo}
          reportConstants={reportConstant}
          selectedPrice={selectedPrice}
          setPriceForBackend={setSelectedPrice}
          priceWithoutMarketPrise={priceWithoutMarketPrise}
          setPriceWithoutMarketPrise={setPriceWithoutMarketPrise}
        />
      </div>

      <BottomSheet open={isBottomSheetOpen} onDismiss={handleDismissBottomSheet}>
        <BottomSheetResult type='info' header='Заявка принята'>
          Ваша заявка отправлена на модерацию
        </BottomSheetResult>
        <Button label='Отлично' onClick={handleDismissBottomSheet} variant={'gray'} />
        <Button
          label='Загрузить фотографию'
          onClick={handleUploadPhoto}
          className={styles.button}
          variant={'secondary'}
        />
      </BottomSheet>
    </BaseScreen>
  );
};

export default ChoosePriceSecondary;
